// @flow
import React from 'react'
import { Link } from 'react-router-dom'
import Row from 'components/References/Row'
import IconSwitch from 'components/IconSwitch/IconSwitch'
import styles from './styles'
import formatters from 'lib/formatters'
import { has } from 'lodash'

type Props = {
  status: string,
  ageAtOpen?: number | null,
  feeSchedule?: string | null,
  hasCustomFeeSchedule: any,
  hasNonCashAssets: boolean,
  isBurialPrepaid: boolean,
  isRestricted: boolean,
  hasHeader?: string | null,
  potentialClientId?: number | null,
  trustName: string | null,
  productDescription?: string | null,
  hasBudget: boolean,
  hasPublicGuardian: boolean,
  hasDonationClient: boolean,
  hasSpecialTaxSituation: boolean,
  spendingPlan: number | null,
  floor: number | null,
  hasParticipatingOrg: boolean,
  hasLien: boolean,
  hasTeams: boolean,
  hasGTHiredCareManagement: boolean,
  pCardLast4: string | null,
  isQIT: boolean,
  hasSubmissionToJurisdiction: boolean,
  guardianshipAdminCounty: string | null,
  initialDepositAmount: number | null,
  monthlyIncomeAmount: number | null,
}

const AccountExtrasRef = (props: Props) => {
  const {
    status,
    ageAtOpen,
    feeSchedule,
    hasCustomFeeSchedule,
    hasNonCashAssets,
    isBurialPrepaid,
    isRestricted,
    hasHeader,
    potentialClientId,
    trustName,
    productDescription,
    hasBudget,
    hasPublicGuardian,
    hasDonationClient,
    hasSpecialTaxSituation,
    spendingPlan,
    floor,
    hasParticipatingOrg,
    hasLien,
    hasTeams,
    hasGTHiredCareManagement,
    pCardLast4,
    isQIT,
    hasSubmissionToJurisdiction,
    guardianshipAdminCounty,
    initialDepositAmount,
    monthlyIncomeAmount
  } = props

  let cardStyle, header
  if (hasHeader) {
    cardStyle = styles.cardMarginBot
    header = (
      <div className={styles.titleSection}>
        <h5>Account Extras</h5>
      </div>
    )
  }

  let clientIntakeRow
  if (potentialClientId) {
    const link =
      <Link
        to={`/potential_clients/edit/${potentialClientId}#distributions`}
        target="_blank"
      >
        See client intake form
      </Link>

    clientIntakeRow = (
      <Row
        singleLine
        label="Distributions"
        val={link}
      />
    )
  }

  return (
    <div className={cardStyle}>
      {header}
      <div className={styles.accountExtrasSection}>
        <Row
          singleLine
          label='Product'
          val={productDescription}
        />
        {trustName &&
          <Row
            singleLine
            label="Trust Name"
            val={trustName}
          />
        }
        {hasParticipatingOrg && (
          <Row
            singleLine
            label="Participating Organization"
            val={<IconSwitch flag={hasParticipatingOrg} />}
          />
        )}
        <Row
          singleLine
          label="Age at account opening"
          val={ageAtOpen}
        />
        {initialDepositAmount && (
          <Row
            singleLine
            label="Initial Deposit Amount"
            val={formatters.money(initialDepositAmount)}
          />
        )}
        <Row
          singleLine
          label="Monthly Income"
          val={formatters.money(monthlyIncomeAmount)}
        />
        {isQIT && (
          <Row
            singleLine
            label="QIT"
            val={<IconSwitch flag={isQIT} />}
          />
        )}
        {hasNonCashAssets && (
          <Row
            singleLine
            label="Non-Cash Assets"
            val={<IconSwitch flag={hasNonCashAssets} />}
          />
        )}
        {hasBudget && (
          <Row
            singleLine
            label="Budget"
            val={<IconSwitch flag={hasBudget} />}
          />
        )}
        {spendingPlan && (
          <Row
            singleLine
            label="Budget (monthly)"
            val={formatters.money(spendingPlan)}
          />
        )}
        {floor && (
          <Row
            singleLine
            label="Floor"
            val={formatters.money(floor)}
          />
        )}
        {guardianshipAdminCounty && (
          <Row
            singleLine
            label="County in which the guardianship is administered"
            val={guardianshipAdminCounty}
          />
        )}
        {hasSubmissionToJurisdiction && (
          <Row
            singleLine
            label="Submission to jurisdiction of the court"
            val={<IconSwitch flag={hasSubmissionToJurisdiction} />}
          />
        )}
        {isBurialPrepaid && (
          <Row
            singleLine
            label="Prepaid Burial"
            val={<IconSwitch flag={isBurialPrepaid} />}
          />
        )}
        {pCardLast4 && (
          <Row
            singleLine
            label="P-Card Last 4"
            val={pCardLast4}
          />
        )}
        {hasTeams && (
          <Row
            singleLine
            label="TEAMS"
            val={<IconSwitch flag={hasTeams} />}
          />
        )}
        {hasGTHiredCareManagement && (
          <Row
            singleLine
            label="GT Hired Care Management"
            val={<IconSwitch flag={hasGTHiredCareManagement} />}
          />
        )}
        {feeSchedule && (
          <Row
            singleLine
            label="Fee Schedule"
            val={formatters.legibleFeeSchedule(feeSchedule)}
          />
        )}
        {hasCustomFeeSchedule && (
          <Row
            singleLine
            label="Custom Fee Schedule"
            val={<IconSwitch flag={hasCustomFeeSchedule} />}
          />
        )}
        {isRestricted && (
          <Row
            singleLine
            label="Restricted"
            val={<IconSwitch flag={isRestricted} />}
          />
        )}
        {hasPublicGuardian && (
          <Row
            singleLine
            label="Public Guardian"
            val={<IconSwitch flag={hasPublicGuardian} />}
          />
        )}
        {hasDonationClient && (
          <Row
            singleLine
            label="Donation Client"
            val={<IconSwitch flag={hasDonationClient} />}
          />
        )}
        {hasSpecialTaxSituation && (
          <Row
            singleLine
            label="Special Tax Situation"
            val={<IconSwitch flag={hasSpecialTaxSituation} />}
          />
        )}
        {hasLien && (
          <Row
            singleLine
            label="Lien"
            val={<IconSwitch flag={hasLien} />}
          />
        )}
        {clientIntakeRow}
      </div>
    </div>
  )
}

export default AccountExtrasRef
